import { Directive, effect, forwardRef, input } from '@angular/core';
import {
    AbstractControl,
    NG_VALIDATORS,
    ValidationErrors,
    Validator,
    ValidatorFn,
} from '@angular/forms';

/**
 * Validator function for checking if the HTML value is empty or contains only whitespace.
 *
 * @returns validator function
 */
export function whitespaceOrEmptyHtmlValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const isEmptyOrWhitespace =
            stripHtmlTags((control.value || '') as string).trim().length === 0;

        return isEmptyOrWhitespace ? { required: true } : null;
    };
}

/**
 * Strips HTML tags and returns the plain text content.
 *
 * @param html The HTML string to strip.
 * @returns Plain text content.
 */
export function stripHtmlTags(html: string): string {
    if (typeof DOMParser !== 'undefined') {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || '';
    }

    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
}

/**
 * Validator directive for checking if the HTML value is empty or contains only whitespace.
 *
 * @usageNotes Meant for template driven forms.
 *
 * @example
 * <quill-editor whitespaceOrEmptyHtml />
 * <quill-editor whitespaceOrEmptyHtml [whitespaceOrEmptyHtmlApplyCondition]="lang === DEFAULT_LANG" />
 */
@Directive({
    selector: '[whitespaceOrEmptyHtml]',
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(
                () => WhitespaceOrEmptyHtmlValidatorDirective
            ),
            multi: true,
        },
    ],
})
export class WhitespaceOrEmptyHtmlValidatorDirective implements Validator {
    readonly applyCondition = input(true, {
        alias: 'whitespaceOrEmptyHtmlApplyCondition',
    });

    private validatorChange?: () => void;

    constructor() {
        effect(() => {
            this.applyCondition();
            this.validatorChange?.();
        });
    }

    validate(control: AbstractControl): ValidationErrors | null {
        return this.applyCondition()
            ? whitespaceOrEmptyHtmlValidator()(control)
            : null;
    }

    registerOnValidatorChange(fn: () => void): void {
        this.validatorChange = fn;
    }
}
